import Form from "components/Forms/Form";
import Input from "components/Forms/Input";
import Select, { OptionInterface } from "components/Forms/Select";
import Pagination from "components/Pagination";
import { routes } from "components/Routes";
import Table from "components/Table";
import Cell from "components/Table/Cell";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { generatePath, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { shopifyProductStore } from "stores/ShopifyProductStore";
import { ShopifyProductInterface } from "types/ShopifyProductInterface";
import { MiniProgressSpinner } from "components/MiniProgressSpinner";

const PageSynchronizeProducts = observer(() => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const [page, setPage] = useState<number>(parseInt(searchParams.get('page') ?? '1'));
    const [approveInProgress, setApproveInProgress] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string | null>(searchParams.get('search'));
    const [actionQuery, setActionQuery] = useState<string | null>(searchParams.get('takeAction'));
    const [selection, setSelection] = useState<ShopifyProductInterface[]>([]);

    // when searchParams changes
    useEffect(() => {
        // pagination
        let pageQuery = searchParams.get('page') ?? '1';
        setPage(parseInt(pageQuery));

        // filters
        setSearchQuery(searchParams.get('search'));
        setActionQuery(searchParams.get('takeAction'));
        setSelection([]);

    }, [searchParams])

    // when page, search or action states changes
    useEffect(() => {
        shopifyProductStore.getShopifyProducts(page > 1 ? page : 1, actionQuery, searchQuery);
    }, [page, searchQuery, actionQuery])

    // resetter
    useEffect(() => {
        return () => {
            shopifyProductStore.resetStore();
        }
    }, [])

    const shopifyProducts = shopifyProductStore.shopifyProducts?.items;

    const disableApproveSelection = () => {
        return (actionQuery !== null && actionQuery === 'ignore') // filtered by take action = ignore
            || (!shopifyProducts || shopifyProducts.length === 0) // no shopify products
            || approveInProgress // approve already in progress
            || selection.length === 0; // no selection
    }

    const approveSelection = async () => {
        if (!window.confirm(`Please confirm:\n\nALL selected products will be synchronized.\n\nWarning: This action cannot be undone!`)) return;

        setApproveInProgress(true);
        await shopifyProductStore.approveSelection(selection.map((item: ShopifyProductInterface) => item.gid));

        // reset after approve selection
        shopifyProductStore.resetStore();
        shopifyProductStore.getShopifyProducts(1, actionQuery, searchQuery);
        setSelection([]);
        setApproveInProgress(false);
    }

    const filter = (data: { search: string, takeAction: string }) => {
        let url = location.pathname + '?page=1';
        if (data.search) {
            url += '&search=' + data.search;
        }
        if (data.takeAction) {
            url += '&takeAction=' + data.takeAction;
        }

        const dataSearch = (typeof data.search != 'undefined' && data.search ? data.search : null);
        const dataTakeAction = (typeof data.takeAction != 'undefined' && data.takeAction ? data.takeAction : null);

        // only navigate if query is different from current
        // (navigate doesn't trigger resetStore because it's the same path)
        if (dataSearch !== searchQuery || dataTakeAction !== actionQuery) {
            shopifyProductStore.resetStore();
            navigate(url);
        }
    }

    const takeActionOptions: Array<OptionInterface> = [
        {
            id: 'create',
            value: 'Create'
        },
        {
            id: 'update',
            value: 'Update'
        },
        {
            id: 'archive',
            value: 'Archive'
        },
        {
            id: 'ignore',
            value: 'Ignore'
        }
    ]

    return (
        <div>
            <h1>Synchronize Products</h1>
            <Form onSubmit={(data) => filter(data)} >
                <div className="mb-8">
                    <Input
                        placeholder="Lookup Title, Handle, Product Type, Id or Tags"
                        name="search"
                        className="w-4/12 float-left"
                        defaultValue={searchQuery ?? undefined}
                    />
                    <Select
                        placeholder="Sync. action (all)"
                        options={takeActionOptions}
                        name="takeAction"
                        className="w-2/12 float-left ml-4"
                        defaultValue={actionQuery ?? undefined}
                    />
                    <button type="submit" className="ml-4 btn-primary">Filter</button>
                    <button type="button" className="btn-primary float-right" disabled={disableApproveSelection()} onClick={approveSelection}>
                        {
                            approveInProgress
                                ? <><MiniProgressSpinner /> <span>Approving...</span></>
                                : `Approve (${selection.length})`
                        }
                    </button>
                </div>

            </Form>
            {
                shopifyProducts &&
                shopifyProducts.length > 0 &&
                <Table
                    data={shopifyProducts}
                    head={[
                        { label: 'Title', sortProperty: '' },
                        { label: 'Handle', sortProperty: '' },
                        { label: 'Product Type', sortProperty: '' },
                        { label: 'Status', sortProperty: '' },
                        { label: 'Id (Shopify)', sortProperty: '' },
                        { label: 'Shop', sortProperty: '' },
                        { label: 'Sync. action', sortProperty: '' },
                    ]}
                    defaultSortProperty={""}
                    defaultSortDirection={"desc"}
                    selectAll={(selection) => setSelection([...selection])}
                    onRowChecked={(selection) => setSelection([...selection])}
                    onRowClicked={(shopifyProduct: ShopifyProductInterface) => navigate(generatePath(routes.pageSynchronizeProductDetails, { gid: shopifyProduct.gid }))}
                    renderRow={(shopifyProduct: ShopifyProductInterface) => {
                        return (
                            <>
                                <Cell>{shopifyProduct.title}</Cell>
                                <Cell>{shopifyProduct.handle}</Cell>
                                <Cell>{shopifyProduct.productType}</Cell>
                                <Cell>{shopifyProduct.status}</Cell>
                                <Cell>{shopifyProduct.id}</Cell>
                                <Cell>{shopifyProduct.shopifyShop?.name}</Cell>
                                <Cell>{shopifyProduct.takeAction}</Cell>
                            </>
                        )
                    }}
                />
            }
            {
                shopifyProductStore.shopifyProducts?.pagination &&
                <Pagination pagination={shopifyProductStore.shopifyProducts.pagination} />
            }
        </div>
    )
})

export default PageSynchronizeProducts;